import Button from '@rio-cloud/rio-uikit/Button';
import { FunctionComponent } from 'react';
import { assertExhaustive } from '../../../../../utils';
import { NoValue } from '../../../sharedComponents/NoValue';
import { DateTime } from '../../../sharedComponents/table/BaseTableComponents';
import { IncomingVehicle } from '../../compoundManagement.types';
import {
    DisplayArrivalFreightForwarder,
    DisplayArrivalFromCity,
    DisplayArrivalFromLocationId,
} from '../../shared/dataDisplay/ArrivalDataContent';
import { DisplayCancellableData } from '../../shared/dataDisplay/DisplayCancellableData';
import { DisplayDispatchType } from '../../shared/dataDisplay/DisplayDispatchType';
import { DisplayFuelType } from '../../shared/dataDisplay/DisplayFuelType';
import {
    DisplayMeansOfTransportId,
    DisplayMeansOfTransportType,
} from '../../shared/dataDisplay/DisplayMeansOfTransportType';
import { DisplayModelName } from '../../shared/dataDisplay/DisplayModelName';
import { CompoundVehicleActionButton } from '../../shared/table/CompoundVehicleActionButton';
import { IncomingVehiclesTableColumn } from './IncomingVehiclesTableColumn';

type TableDataContentProps = { column: IncomingVehiclesTableColumn; vehicle: IncomingVehicle };

export const IncomingVehiclesTableDataContent: FunctionComponent<TableDataContentProps> = ({ column, vehicle }) => {
    const { isCancelled, editable } = vehicle.arrival;

    switch (column) {
        case IncomingVehiclesTableColumn.VIN:
            return <div className="ellipsis-1">{vehicle.vehicle.vin}</div>;
        case IncomingVehiclesTableColumn.LOCATION_ID:
            return <span>{vehicle.compoundLocation.identifier}</span>;
        case IncomingVehiclesTableColumn.LOCATION_CITY:
            return <span>{vehicle.compoundLocation.address.city}</span>;
        case IncomingVehiclesTableColumn.EXPECTED_ARRIVAL_DATE:
            return (
                <DisplayCancellableData isCancelled={isCancelled}>
                    <DateTime
                        dateTime={vehicle.arrival.expectedArrivalAt}
                        timeZone={vehicle.compoundLocation.timeZone}
                    />
                </DisplayCancellableData>
            );
        case IncomingVehiclesTableColumn.ARRIVAL_FROM_LOCATION_ID:
            return <DisplayArrivalFromLocationId compoundVehicle={vehicle} />;
        case IncomingVehiclesTableColumn.ARRIVAL_FROM_CITY:
            return <DisplayArrivalFromCity compoundVehicle={vehicle} />;
        case IncomingVehiclesTableColumn.MANUFACTURER:
            return vehicle.vehicle.model.manufacturer ?? <NoValue />;
        case IncomingVehiclesTableColumn.MODEL:
            return <DisplayModelName vehicle={vehicle.vehicle} />;
        case IncomingVehiclesTableColumn.DISPATCH_TYPE:
            return <DisplayDispatchType vehicle={vehicle.vehicle} />;
        case IncomingVehiclesTableColumn.FUEL_TYPE:
            return <DisplayFuelType vehicle={vehicle.vehicle} />;
        case IncomingVehiclesTableColumn.MODEL_VERSION:
            return vehicle.vehicle.model.version ?? <NoValue />;
        case IncomingVehiclesTableColumn.ARRIVAL_FREIGHT_FORWARDER:
            return <DisplayArrivalFreightForwarder arrival={vehicle.arrival} />;
        case IncomingVehiclesTableColumn.MEANS_OF_TRANSPORT:
            return (
                <DisplayMeansOfTransportId
                    meansOfTransportId={vehicle.arrival.transportInformation.meansOfTransportId}
                    isCancelled={isCancelled}
                />
            );
        case IncomingVehiclesTableColumn.MEANS_OF_TRANSPORT_TYPE:
            return (
                <DisplayCancellableData isCancelled={isCancelled}>
                    <DisplayMeansOfTransportType
                        meansOfTransportType={vehicle.arrival.transportInformation.meansOfTransportType}
                    />
                </DisplayCancellableData>
            );
        case IncomingVehiclesTableColumn.ACTION:
            return (
                isCancelled ||
                !editable || (
                    <CompoundVehicleActionButton
                        labelId="outboundPortal.compoundManagement.button.confirmArrival"
                        icon="rioglyph-load"
                        vehicle={vehicle}
                        buttonStyle={Button.PRIMARY}
                    />
                )
            );
        default:
            assertExhaustive(column);
    }
};
